import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Share2, Check, Lock, Globe } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import VideoPlayer from '../components/VideoPlayer';
import FeedbackList from '../components/FeedbackList';
import FeedbackForm from '../components/FeedbackForm';
import SubtitleEditor from '../components/SubtitleEditor';
import AnnotationCanvas from '../components/AnnotationCanvas';
import { Pencil, X, FileText } from 'lucide-react';
import { generatePDFReport } from '../utils/pdfHelper';

export default function ReviewPage() {
    const { reviewId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const playerRef = useRef(null);
    const saveTimeoutRef = useRef(null);

    const [review, setReview] = useState(null);
    const [project, setProject] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [feedbacks, setFeedbacks] = useState([]);
    const [subtitles, setSubtitles] = useState([]);
    const [mode, setMode] = useState('comments'); // 'comments' | 'subtitles'
    const [loading, setLoading] = useState(true);
    const [saveStatus, setSaveStatus] = useState(''); // 'saving', 'saved', ''
    const feedbacksDirtyRef = useRef(false);
    const subtitlesDirtyRef = useRef(false);

    // Annotation State
    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });
    const [screenShot, setScreenShot] = useState(null); // Base64 image
    const [overlayImage, setOverlayImage] = useState(null); // View mode overlay
    const videoContainerRef = useRef(null);
    const canvasRef = useRef(null);

    // Measure video container on resize
    useEffect(() => {
        if (!videoContainerRef.current) return;
        const observer = new ResizeObserver(([entry]) => {
            setVideoDimensions({
                width: entry.contentRect.width,
                height: entry.contentRect.height
            });
        });
        observer.observe(videoContainerRef.current);
        return () => observer.disconnect();
    }, [loading]);

    // Load Data
    useEffect(() => {
        const loadReview = async () => {
            setLoading(true);
            try {
                const reviewRef = doc(db, 'reviews', reviewId);
                const reviewSnap = await getDoc(reviewRef);

                if (reviewSnap.exists()) {
                    const reviewData = reviewSnap.data();

                    // Privacy Check
                    if (reviewData.visibility === 'private' && !currentUser) {
                        alert("Private Video. Please Login.");
                        navigate('/login');
                        return;
                    }

                    setReview({ id: reviewSnap.id, ...reviewData });
                    setFeedbacks(reviewData.feedbacks || []);
                    setSubtitles(reviewData.subtitles || []);

                    // Load Project
                    if (reviewData.projectId) {
                        const projectSnap = await getDoc(doc(db, 'projects', reviewData.projectId));
                        if (projectSnap.exists()) {
                            const projectData = projectSnap.data();

                            // Cascading Project Privacy Check
                            if (projectData.visibility === 'private' && !currentUser) {
                                alert("This Project is Locked. Please Login.");
                                navigate('/login');
                                return;
                            }

                            setProject({ id: projectSnap.id, ...projectData });
                        }
                    }
                } else {
                    navigate('/');
                }
            } catch (error) {
                console.error("Error loading review:", error);
            }
            setLoading(false);
        };
        loadReview();
    }, [reviewId, navigate, currentUser]);

    // Auto-save logic
    const saveReviewData = useCallback(async (dataToUpdate) => {
        setSaveStatus('saving');
        try {
            await updateDoc(doc(db, 'reviews', reviewId), {
                ...dataToUpdate,
                updatedAt: new Date().toISOString()
            });
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(''), 2000);
        } catch (error) {
            console.error("Error saving:", error);
            setSaveStatus('error');
        }
    }, [reviewId]);

    // Mark feedbacks dirty when changed (skip on initial load)
    const isFirstFeedbackRender = useRef(true);
    useEffect(() => {
        if (isFirstFeedbackRender.current) { isFirstFeedbackRender.current = false; return; }
        feedbacksDirtyRef.current = true;
    }, [feedbacks]);

    const isFirstSubtitleRender = useRef(true);
    useEffect(() => {
        if (isFirstSubtitleRender.current) { isFirstSubtitleRender.current = false; return; }
        subtitlesDirtyRef.current = true;
    }, [subtitles]);

    // Debounced save — only writes fields that actually changed
    useEffect(() => {
        if (loading || !review) return;
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

        saveTimeoutRef.current = setTimeout(() => {
            const payload = {};
            if (feedbacksDirtyRef.current) { payload.feedbacks = feedbacks; feedbacksDirtyRef.current = false; }
            if (subtitlesDirtyRef.current) { payload.subtitles = subtitles; subtitlesDirtyRef.current = false; }
            if (Object.keys(payload).length > 0) saveReviewData(payload);
        }, 1500);

        return () => clearTimeout(saveTimeoutRef.current);
    }, [feedbacks, subtitles, saveReviewData, review, loading]);


    const handleTimeUpdate = (time) => {
        setCurrentTime(time);
    };

    const handleSeek = (time) => {
        playerRef.current?.seekTo(time);
        setCurrentTime(time);
        setOverlayImage(null);
    };

    const handleFeedbackSelect = (feedback) => {
        playerRef.current?.seekTo(feedback.timestamp);
        setCurrentTime(feedback.timestamp);

        if (feedback.screenshot) {
            setOverlayImage(feedback.screenshot);
            playerRef.current?.pauseVideo();
        } else {
            setOverlayImage(null);
        }
    };

    // Clear overlay when video starts playing
    useEffect(() => {
        if (isPlaying) {
            setOverlayImage(null);
        }
    }, [isPlaying]);

    // Feedback Actions
    const handleAddFeedback = ({ text, authorName }) => {
        const newFeedback = {
            id: crypto.randomUUID(),
            timestamp: currentTime,
            text: text,
            authorName: authorName,
            isResolved: false,
            reply: '',
            createdAt: new Date().toISOString(),
            screenshot: screenShot // Attach screenshot if exists
        };

        const updated = [...feedbacks, newFeedback].sort((a, b) => a.timestamp - b.timestamp);
        setFeedbacks(updated);

        // Reset after submit
        setScreenShot(null);
        if (canvasRef.current) canvasRef.current.clearCanvas();
        setIsDrawingMode(false);
    };

    const handleCaptureDrawing = () => {
        if (canvasRef.current) {
            const dataUrl = canvasRef.current.getDataURL();
            // Check if empty? We trust user drawn something.
            setScreenShot(dataUrl);
            setIsDrawingMode(false); // Optimize: Switch off drawing mode
            // Maybe scroll to form?
            alert("Drawing captured! Now add your comment.");
        }
    };

    const handleCancelDrawing = () => {
        setIsDrawingMode(false);
        setScreenShot(null);
        if (canvasRef.current) canvasRef.current.clearCanvas();
    };

    const handleResolve = (id) => {
        setFeedbacks(feedbacks.map(f =>
            f.id === id ? { ...f, isResolved: !f.isResolved } : f
        ));
    };

    const handleDelete = (id) => {
        if (window.confirm("Remove this feedback?")) {
            setFeedbacks(feedbacks.filter(f => f.id !== id));
        }
    };

    const handleEdit = (id, newText) => {
        setFeedbacks(feedbacks.map(f =>
            f.id === id ? { ...f, text: newText } : f
        ));
    };

    const handleToggleVisibility = async () => {
        if (!review) return;

        const newVisibility = review.visibility === 'private' ? 'public' : 'private';
        const confirmMsg = newVisibility === 'private'
            ? "Lock this video? (Only logged-in users will see it)"
            : "Make this video Public? (Anyone with link can see it)";

        if (!window.confirm(confirmMsg)) return;

        try {
            await updateDoc(doc(db, 'reviews', review.id), {
                visibility: newVisibility,
                updatedAt: new Date().toISOString()
            });
            setReview({ ...review, visibility: newVisibility });
        } catch (error) {
            console.error("Error updating visibility:", error);
            alert("Failed to update visibility");
        }
    };

    const handleReply = (id, replyText) => {
        setFeedbacks(feedbacks.map(f =>
            f.id === id ? { ...f, reply: replyText } : f
        ));
    };

    const copyShareLink = () => {
        const url = window.location.href;
        navigator.clipboard.writeText(url);
        alert("Link copied to clipboard!");
    };

    const activeSubtitle = subtitles.find(sub =>
        currentTime >= sub.startTime && currentTime <= sub.endTime
    );

    if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white">Loading...</div>;
    if (!review) return null;

    return (
        <Layout projectTitle={project?.title} reviewTitle={review.title} projectId={project?.id}>

            <div className={`grid grid-cols-1 gap-8 transition-all duration-300 ${mode === 'subtitles' ? 'lg:grid-cols-5' : 'lg:grid-cols-3'
                }`}>
                {/* Left Column: Video */}
                <div className={`space-y-4 transition-all duration-300 ${mode === 'subtitles' ? 'lg:col-span-3' : 'lg:col-span-2'
                    }`}>
                    <div
                        ref={videoContainerRef}
                        className="relative rounded-xl overflow-hidden bg-black aspect-video group"
                    >
                        {/* Annotation Layer */}
                        <div className="absolute top-0 left-0 z-10 w-full h-full pointer-events-none">
                            {/* Only render canvas if we have dimensions (avoids 0x0 canvas) */}
                            {videoDimensions.width > 0 && (
                                <div className={isDrawingMode ? "pointer-events-auto" : "pointer-events-none"}>
                                    <AnnotationCanvas
                                        ref={canvasRef}
                                        width={videoDimensions.width}
                                        height={videoDimensions.height}
                                        isDrawingMode={isDrawingMode}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Viewing Mode Overlay (Saved Annotation) */}
                        {overlayImage && !isDrawingMode && (
                            <div
                                className="absolute top-0 left-0 z-50 w-full h-full pointer-events-none fade-in"
                            >
                                <img
                                    src={overlayImage}
                                    className="w-full h-full object-contain"
                                    alt="Correction Overlay"
                                />
                            </div>
                        )}

                        <VideoPlayer
                            ref={playerRef}
                            videoId={review.videoId}
                            onTimeUpdate={handleTimeUpdate}
                            onStateChange={setIsPlaying}
                        />

                        {/* Drawing Controls Overlay */}
                        {isDrawingMode && (
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 gap-4 flex z-30">
                                <button
                                    onClick={handleCaptureDrawing}
                                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full font-bold shadow-lg flex items-center gap-2"
                                >
                                    <Check size={18} /> Done
                                </button>
                                <button
                                    onClick={handleCancelDrawing}
                                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-full font-bold shadow-lg flex items-center gap-2"
                                >
                                    <X size={18} /> Cancel
                                </button>
                            </div>
                        )}
                    </div>

                    {!isDrawingMode && mode !== 'subtitles' && (
                        <div className="flex justify-center mt-2">
                            <button
                                onClick={() => {
                                    playerRef.current?.pauseVideo();
                                    setIsDrawingMode(true);
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 border border-purple-500/50 rounded-lg transition-all"
                            >
                                <Pencil size={18} /> Draw on Video
                            </button>
                        </div>
                    )}

                    {mode === 'subtitles' && (
                        <div className="min-h-[80px] flex items-center justify-center p-6 bg-black/40 rounded-xl border border-cyan-500/30 text-center transition-all">
                            {activeSubtitle ? (
                                <p className="text-xl md:text-2xl font-medium text-cyan-400 font-mono leading-relaxed drop-shadow-lg">
                                    {activeSubtitle.text}
                                </p>
                            ) : (
                                <p className="text-gray-500 italic text-sm">...</p>
                            )}
                        </div>
                    )}

                    <div className="flex items-center justify-between p-4 bg-[#1a1a1a] rounded-xl border border-white/10">
                        <div>
                            <h2 className="text-xl font-bold text-white max-w-md truncate">{review.title}</h2>
                            <div className="flex items-center gap-2 text-sm mt-1">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${saveStatus === 'saved' ? 'bg-green-500/20 text-green-400' : 'bg-gray-800 text-gray-400'}`}>
                                    {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Auto-save'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {currentUser && (
                            <button
                                onClick={handleToggleVisibility}
                                className={`px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-2 transition-all border ${review.visibility === 'private'
                                    ? 'bg-red-500/10 text-red-400 border-red-500/50 hover:bg-red-500/20'
                                    : 'bg-green-500/10 text-green-400 border-green-500/50 hover:bg-green-500/20'
                                    }`}
                            >
                                {review.visibility === 'private' ? <Lock size={14} /> : <Globe size={14} />}
                                {review.visibility === 'private' ? 'Private' : 'Public'}
                            </button>
                        )}

                        <button
                            onClick={copyShareLink}
                            className="px-4 py-2 bg-white/5 hover:bg-cyan-500/20 text-white hover:text-cyan-400 rounded-lg font-medium transition-colors flex items-center gap-2"
                        >
                            <Share2 size={18} /> Share
                        </button>
                    </div>
                </div>


                {/* Right Column: Feedback / Subtitles */}
                <div className={`h-[calc(100vh-140px)] sticky top-6 flex flex-col transition-all duration-300 ${mode === 'subtitles' ? 'lg:col-span-2' : 'lg:col-span-1'
                    }`}>

                    {/* Mode Toggle */}
                    <div className="flex p-1 bg-white/5 rounded-lg mb-4 border border-white/5">
                        <button
                            onClick={() => setMode('comments')}
                            className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${mode === 'comments'
                                ? 'bg-[#1a1a1a] text-white shadow-lg border border-white/10'
                                : 'text-gray-500 hover:text-gray-300'
                                }`}
                        >
                            Comments ({feedbacks.length})
                        </button>
                        <button
                            onClick={() => setMode('subtitles')}
                            className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${mode === 'subtitles'
                                ? 'bg-[#1a1a1a] text-white shadow-lg border border-white/10'
                                : 'text-gray-500 hover:text-gray-300'
                                }`}
                        >
                            Subtitles ({subtitles.length})
                        </button>
                    </div>

                    {mode === 'comments' ? (
                        <>
                            <FeedbackForm
                                currentTime={currentTime}
                                onSubmit={handleAddFeedback}
                                screenshot={screenShot}
                                onClearScreenshot={() => setScreenShot(null)}
                            />

                            <div className="flex-1 overflow-hidden bg-[#1a1a1a]/50 rounded-xl border border-white/5 p-4 flex flex-col mt-4">
                                <div className="flex justify-between items-center mb-4 pb-2 border-b border-white/5">
                                    <h3 className="font-bold text-gray-300">Feedback List</h3>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => generatePDFReport(review, project, feedbacks)}
                                            className="text-xs flex items-center gap-1 text-cyan-400 hover:text-cyan-300 transition-colors"
                                            title="Download PDF Report"
                                        >
                                            <FileText size={14} /> Export PDF
                                        </button>
                                        <div className="text-xs text-gray-500">
                                            {feedbacks.filter(f => f.isResolved).length} resolved
                                        </div>
                                    </div>
                                </div>

                                <FeedbackList
                                    feedbacks={feedbacks}
                                    currentTime={currentTime}
                                    onSeek={handleFeedbackSelect}
                                    onResolve={handleResolve}
                                    onDelete={handleDelete}
                                    onEdit={handleEdit}
                                    onReply={handleReply}
                                />
                            </div>
                        </>
                    ) : (
                        <SubtitleEditor
                            subtitles={subtitles}
                            setSubtitles={setSubtitles}
                            currentTime={currentTime}
                            onSeek={handleSeek}
                        />
                    )}
                </div>
            </div>
        </Layout >
    );
}
